import type { SubUserScope } from './reqres';

export const SUB_USER_SCOPE_READ = 'read' satisfies SubUserScope;
export const SUB_USER_SCOPE_WRITE = 'write' satisfies SubUserScope;
export const SUB_USER_SCOPE_TRADE = 'write::trade' satisfies SubUserScope;
export const SUB_USER_SCOPE_TRANSFER = 'write::transfer' satisfies SubUserScope;

export function hasReadScope(scopes: readonly string[]) {
    return scopes.includes(SUB_USER_SCOPE_READ);
}

export function hasTradeScope(scopes: readonly string[]) {
    return scopes.includes(SUB_USER_SCOPE_WRITE) || scopes.includes(SUB_USER_SCOPE_TRADE);
}

export function hasBroadWriteScope(scopes: readonly string[]) {
    return scopes.includes(SUB_USER_SCOPE_WRITE);
}

export function hasTransferScope(scopes: readonly string[]) {
    return scopes.includes(SUB_USER_SCOPE_WRITE) || scopes.includes(SUB_USER_SCOPE_TRANSFER);
}

/**
 * Selecting a subaccount is a read-level capability. Mutations performed after
 * the switch remain protected by their own trade/transfer scopes.
 */
export function canSwitchSubaccounts(isSubUser: boolean, scopes: readonly string[]) {
    return !isSubUser || hasReadScope(scopes);
}

export function getDisplaySubUserScopes(scopes: readonly string[]): SubUserScope[] {
    const displayScopes: SubUserScope[] = [];
    if (hasReadScope(scopes)) displayScopes.push(SUB_USER_SCOPE_READ);

    // Broad parent `write` is parent-user-only and shouldn't be granted to sub-users.
    // Surface any legacy broad-write scope explicitly rather than decomposing it into
    // Trade/Transfers, so admins can spot sub-users that still hold it and re-scope them.
    if (hasBroadWriteScope(scopes)) {
        displayScopes.push(SUB_USER_SCOPE_WRITE);
        return displayScopes;
    }

    if (hasTradeScope(scopes)) displayScopes.push(SUB_USER_SCOPE_TRADE);
    if (hasTransferScope(scopes)) displayScopes.push(SUB_USER_SCOPE_TRANSFER);

    return displayScopes;
}

/**
 * Serializes the read/trade/transfers permission toggles into sub-user scopes.
 *
 * Sub-users only ever receive the granular child scopes; broad parent `write` is
 * reserved for the parent user, so editing a legacy broad-write sub-user re-scopes
 * them to `read` + `write::trade` + `write::transfer` rather than preserving `write`.
 */
export function serializeSubUserScopes({
    read,
    write,
    transfers,
}: {
    read: boolean;
    write: boolean;
    transfers: boolean;
}): SubUserScope[] {
    const scopes: SubUserScope[] = [];
    if (read) scopes.push(SUB_USER_SCOPE_READ);
    if (write) scopes.push(SUB_USER_SCOPE_TRADE);
    if (transfers) scopes.push(SUB_USER_SCOPE_TRANSFER);

    return scopes;
}
